import { Link } from "@tanstack/react-router"
import { SearchNormal1 } from "iconsax-reactjs"
import { parseAsString, useQueryState } from "nuqs"
import React, { useEffect, useMemo } from "react"
import PullToRefresh from "react-simple-pull-to-refresh"

import { LoadingSpinner } from "@/components/shared"
import EmptyIcon from "@/components/shared/icons/empty-icon"
import { ToggleFilter } from "@/components/shared/ui"
import { getSubmissionList } from "@/features/jobs/manage/api/manage.api"
import { SUBMISSION_TABS } from "@/features/jobs/manage/constants"
import { useInfinitePageQuery } from "@/modules/helpers/hooks"
import { useAuthStore } from "@/modules/helpers/hooks/shared"
import {
  transformFilterSubmissionToQueryParams,
  useFilterSubmission
} from "@/modules/helpers/hooks/shared/use-filter-submission"
import { sendHaptic } from "@/modules/helpers/utils"

import { FilterSubmission, SubmissionItem, SubmissionItemSkeleton } from "."

// Re-export for backwards compatibility
// Re-export removed

// Constants
const SKELETON_COUNT = 3
const GRID_COLUMNS = "grid-cols-1"

// Types
interface PageHeaderProps {
  totalCount: number
  selectedTab: string
  onTabChange: (value: string) => void
}

interface SubmissionListProps {
  data: any[] | undefined
  forceRefetch: () => void
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  onLoadMore: () => void
}

// Components
const PageHeader: React.FC<PageHeaderProps> = ({ totalCount, selectedTab, onTabChange }) => (
  <div className="sticky top-0 p-2 z-20 bg-white flex flex-col gap-2">
    <div className="flex justify-between items-center gap-2 py-1 mb-2">
      <Link
        to="/s/submission"
        onClick={() => sendHaptic()}
        className="rounded-xl transition-all ease-in-out h-12 z-40 lg:w-[240px] flex-1 gap-2 items-center px-3 flex border border-gray-200 bg-white"
      >
        <SearchNormal1 size={20} color="#0091AA" />
        <p className="text-xs font-light text-[rgba(130,_146,_170,_1)]">Tìm kiếm chiến dịch</p>
      </Link>
      <FilterSubmission />
    </div>

    <ToggleFilter options={SUBMISSION_TABS} value={selectedTab || "ALL"} onChange={onTabChange} />
    <p className="text-xs font-normal">Có {totalCount} chiến dịch</p>
  </div>
)

export const SubmissionSkeleton: React.FC = () => (
  <div className={`lg:w-3/4 lg:mr-auto ${GRID_COLUMNS} grid pb-10 gap-4 mt-4`}>
    {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
      <SubmissionItemSkeleton key={index} />
    ))}
  </div>
)

export const SubmissionList: React.FC<SubmissionListProps> = ({
  data,
  forceRefetch,
  isLoading,
  isFetchingNextPage,
  hasNextPage,
  onLoadMore
}) => {
  const loadMoreRef = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          onLoadMore()
        }
      },
      { threshold: 0.1 }
    )

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, onLoadMore])

  return (
    <PullToRefresh
      onRefresh={async () => {
        forceRefetch()
      }}
      pullingContent={""}
      maxPullDownDistance={80}
      refreshingContent={<LoadingSpinner className="w-20 h-20" />}
    >
      <div className="flex-1 flex flex-col min-h-full">
        {isLoading && (
          <div className=" px-2">
            <SubmissionSkeleton />
          </div>
        )}

        {!data?.length && !isLoading && (
          <div className="flex-1 flex items-center justify-center p-10">
            <div className="flex flex-col items-center justify-center">
              <EmptyIcon />
              <p className="text-primary-800 text-base text-center font-semibold mt-2">
                Chiến dịch chỉ được hiển thị khi được duyệt hoặc từ chối.
              </p>
            </div>
          </div>
        )}

        {!!data?.length && !isLoading && (
          <div className={`${GRID_COLUMNS} grid pb-10 gap-2 px-2 mt-4 `}>
            {data?.map((submission, index) => (
              <SubmissionItem key={submission.id || index} submission={submission} />
            ))}
          </div>
        )}

        {!!data?.length && (
          <div ref={loadMoreRef} className="flex justify-center py-4">
            {isFetchingNextPage && <LoadingSpinner className="h-20 w-20" />}
          </div>
        )}
      </div>
    </PullToRefresh>
  )
}

// Main Component
const ManagePage = () => {
  const { user } = useAuthStore()
  const { filter } = useFilterSubmission()

  const userId = user?.id || ""

  const [selectedTab, setSelectedTab] = useQueryState("tab", parseAsString.withDefault(""))

  // Transform filter state to API query params
  const apiParams = useMemo(() => {
    const transformed = transformFilterSubmissionToQueryParams(filter)
    return {
      status: selectedTab === "ALL" || !selectedTab ? undefined : selectedTab,
      ...transformed
    }
  }, [filter, selectedTab])

  // Data fetching
  const {
    data: dataCampaigns,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    forceRefetch
  } = useInfinitePageQuery({
    queryFn: getSubmissionList,
    params: apiParams,
    queryKey: ["submission", selectedTab, userId, JSON.stringify(filter)]
  })

  // Event handlers
  const handleTabChange = (status: string) => {
    setSelectedTab(status === "ALL" ? "" : status)
  }

  const handleLoadMore = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }

  return (
    <>
      <PageHeader
        totalCount={dataCampaigns?.total || 0}
        selectedTab={selectedTab}
        onTabChange={handleTabChange}
      />
      <SubmissionList
        forceRefetch={forceRefetch}
        data={dataCampaigns?.list}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        onLoadMore={handleLoadMore}
      />
    </>
  )
}

export default ManagePage
